import { useState } from "react";
import type { AddressValue, FormAddressLookup } from "../types.js";
import type { LookupResult } from "../api/formApi.js";
import type { LookupCandidate } from "../schema/lookupModel.js";
import { LOOKUP_MESSAGE_RATE_LIMITED, LOOKUP_MESSAGE_UNAVAILABLE } from "../schema/lookupModel.js";
import { compactForFormat, formatError } from "../schema/formats.js";
import { LookupChoiceList } from "./LookupChoiceList.js";
import { addressGeo, type AddressGeo } from "../schema/formDesign.js";
import "./styles.css";

/**
 * Phase 541 — THE ADDRESS LOOKUP, as the subject meets it: type a postcode, press one button,
 * pick your address off a list, and the five parts fill in.
 *
 * <p><b>It is an OPT-IN accessory to a control that already works.</b> Without a declared
 * `lookup` the block renders byte-identically to the five hand-typed inputs it has always been,
 * and every degraded arm of the search lands back on exactly those inputs — nothing here can
 * disable a field, block the submit button or make an address unanswerable (AC-4, L-49).</p>
 *
 * <p><b>The transport is INJECTED, never reached for.</b> The control takes a plain async
 * callback and knows nothing about the endpoint, the reserved `lookup_id`, the connector or the
 * vendor behind it — task 5.1 owns the single wiring point in `FormExperience`. That is also what
 * makes the busy/candidate state below correct BY CONSTRUCTION for a repeating group: the state
 * is LOCAL to each instance, so two director rows are independent because they are two React
 * instances, not because anything keys a map by field name.</p>
 *
 * <p><b>Phase 564 — a SEARCH-LED block searches first.</b> With the lookup declared AND wired, the
 * postcode and "Find my address" sit on top and the other lines sit below, collapsed until there
 * is something to check: a pick, a value that already carries a line, an `invalid` field, a search
 * that cannot help, or one press of "Enter the address by hand". Once on screen they stay there.
 * Every other block renders exactly the five inputs it always has.</p>
 */

/** The label on the search control. */
export const ADDRESS_LOOKUP_LABEL = "Find my address";
/** Shown on the control while a search is in flight. */
export const ADDRESS_LOOKUP_BUSY_LABEL = "Searching";
/** The prompt above the candidate list. */
export const ADDRESS_LOOKUP_EYEBROW = "Choose your address";
/**
 * The `not_found` arm — and the empty-list arm, which is the same thing to the subject. Plain
 * guidance to carry on by hand, NOT an error: the postcode they typed stays exactly where it is.
 */
export const ADDRESS_LOOKUP_NOT_FOUND =
  "We could not find an address for that postcode. Check it, or fill in your address below.";
/**
 * Phase 564 — the always-present escape on a SEARCH-LED block while its lines are collapsed: one
 * press puts every line on screen, permanently, and spends nothing.
 */
export const ADDRESS_MANUAL_LABEL = "Enter the address by hand";

export interface AddressInputProps {
  id: string;
  value: AddressValue;
  invalid?: boolean;
  describedBy?: string;
  onChange: (value: AddressValue) => void;
  /** KYC-design `same_as`: another address the client may copy with one tick. */
  sameAs?: { label: string; value: AddressValue } | null;
  /** True when the whole address was prefilled by a lookup and is shown read-only. */
  prefilled?: boolean;
  prefilledNote?: string;
  /** Phase 541 — the field's declared `lookup:` kind. ABSENT ⇒ no search control at all. */
  lookup?: FormAddressLookup;
  /**
   * Phase 541 — the INJECTED address-lookup transport: one postcode in, one typed envelope out.
   * It carries no state and no per-field key, so the caller needs neither. ABSENT ⇒ no search
   * control, whatever `lookup` says.
   */
  onLookup?: (postcode: string) => Promise<LookupResult>;
}

/** All five parts as strings — an absent part is `""`, never a missing key (AC-7). */
function addressParts(item: unknown): AddressValue {
  const source = (typeof item === "object" && item !== null ? item : {}) as Record<string, unknown>;
  const part = (key: string) => (typeof source[key] === "string" ? (source[key] as string) : "");
  return {
    line1: part("line1"),
    line2: part("line2"),
    town: part("town"),
    county: part("county"),
    postcode: part("postcode"),
  };
}

/**
 * The connector envelope's `addresses[]`, projected to the candidate list's own shape.
 *
 * <p>This projection is deliberately LOCAL rather than `chooseCandidates` / `lookupPatch`: both of
 * those take a DECLARED `FormLookup` to drive `choose` / `populate`, and the address arm has no
 * declaration on the client at all (the server synthesises one from the reserved id). Inventing a
 * fake `FormLookup` to satisfy them would put a lie in the code to reuse twenty lines.</p>
 *
 * <p>The five-part object rides along as `raw`, so a pick maps back from the OBJECT the subject
 * chose and never from an index into a list that may have been re-fetched.</p>
 */
export function addressCandidates(response: unknown): LookupCandidate[] {
  const envelope = (typeof response === "object" && response !== null ? response : {}) as Record<string, unknown>;
  const addresses = envelope["addresses"];
  if (!Array.isArray(addresses)) return [];
  return addresses.map((item) => {
    const parts = addressParts(item);
    const rest = [parts.line2, parts.town, parts.county, parts.postcode].filter((p) => p!.length > 0).join(", ");
    return {
      title: parts.line1 || parts.town || parts.postcode || "",
      subtitle: rest.length > 0 ? rest : undefined,
      rows: [],
      raw: { ...parts, ...addressGeo(item) },
    };
  });
}

const LINES: ReadonlyArray<{ key: keyof AddressValue; label: string; optional?: boolean; wide?: boolean; mono?: boolean; autoComplete: string }> = [
  { key: "line1", label: "Address line 1", wide: true, autoComplete: "address-line1" },
  { key: "line2", label: "Address line 2", optional: true, wide: true, autoComplete: "address-line2" },
  { key: "town", label: "Town or city", autoComplete: "address-level2" },
  { key: "county", label: "County", optional: true, autoComplete: "address-level1" },
  { key: "postcode", label: "Postcode", mono: true, autoComplete: "postal-code" },
];

function sameAddress(a: AddressValue, b: AddressValue): boolean {
  return LINES.every(({ key }) => (a[key] ?? "").trim() === (b[key] ?? "").trim());
}

/** KYC-design — the structured UK address block. */
export function AddressInput({ id, value, invalid, describedBy, onChange, sameAs, prefilled, prefilledNote, lookup, onLookup }: AddressInputProps) {
  // Local to THIS instance, deliberately (see the note at the top of the file).
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<{ postcode: string; candidates: LookupCandidate[] } | null>(null);
  const [chosen, setChosen] = useState<number | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(null);
  // Phase 564 — the lines of a SEARCH-LED block, once on screen, stay on screen. Set, NEVER
  // cleared: nothing in this control may make an address unanswerable (AC-4, L-49).
  const [revealed, setRevealed] = useState(false);

  const typedPostcode = (value.postcode ?? "").trim();
  const compactPostcode = compactForFormat(typedPostcode);
  // The EXISTING `postcode` preset is the gate, reused rather than re-spelled: an address search
  // is a billable call, so a postcode the form itself would reject can never spend one (AC-3).
  const postcodeValid = typedPostcode.length > 0 && formatError("postcode", typedPostcode) === undefined;
  // The list belongs to the postcode it was found for. Edit the postcode and it is gone; a pick
  // writes the same postcode back, so choosing does not dismiss the list it was chosen from.
  const searched = found !== null && found.postcode === compactPostcode;
  const noticeId = `${id}__lookup-status`;

  const run = async () => {
    if (!onLookup || busy || !postcodeValid || searched) return;
    const asked = compactPostcode;
    setNotice(null);
    setBusy(true);
    let result: LookupResult | undefined;
    try {
      result = await onLookup(typedPostcode);
    } catch {
      // A rejected call is indistinguishable from an unavailable upstream at this layer, and the
      // subject needs the same instruction either way. It is never thrown on.
      result = undefined;
    } finally {
      setBusy(false);
    }

    // Phase 564 — every arm where the search cannot help ALSO reveals the lines, so a degraded
    // search lands the subject on the hand-typed inputs with no further press (AC-4).
    if (result === undefined) {
      setNotice(LOOKUP_MESSAGE_UNAVAILABLE);
      setRevealed(true);
      return;
    }
    if (!result.ok) {
      // The server's own `message` is deliberately NOT read here: an address search must say
      // nothing a vendor gave it — no provider name, no code, no upstream sentence (AC-4).
      setNotice(
        result.outcome === "rate_limited"
          ? LOOKUP_MESSAGE_RATE_LIMITED
          : result.outcome === "not_found"
            ? ADDRESS_LOOKUP_NOT_FOUND
            : LOOKUP_MESSAGE_UNAVAILABLE,
      );
      setRevealed(true);
      return;
    }
    const candidates = addressCandidates(result.response);
    if (candidates.length === 0) {
      setNotice(ADDRESS_LOOKUP_NOT_FOUND);
      setRevealed(true);
      return;
    }
    setFound({ postcode: asked, candidates });
    setChosen(undefined);
  };

  const pick = (index: number) => {
    const candidate = found?.candidates[index];
    if (candidate === undefined) return;
    setChosen(index);
    setNotice(null);
    setRevealed(true);
    // AUTOFILL, NOT LOCK: this writes VALUES and nothing else — no part is marked read-only,
    // disabled or verified, so a picked address is as overwritable as a typed one.
    onChange({ ...(candidate.raw as AddressValue) });
  };

  if (prefilled) {
    return (
      <div className="address-block address-prefilled" id={id} aria-describedby={describedBy}>
        <p className="address-static">
          {[value.line1, value.line2, value.town, value.county].filter((p) => (p ?? "").trim()).map((p) => <span key={p}>{p}<br /></span>)}
          <span className="address-postcode">{value.postcode}</span>
        </p>
        {prefilledNote && <span className="chip">{prefilledNote}</span>}
      </div>
    );
  }
  const copied = sameAs !== null && sameAs !== undefined && sameAddress(value, sameAs.value);
  // Card #1998 / BB walk 2 — a hand edit keeps the finder's coordinates UNLESS it changes the
  // POSTCODE (see `AddressGeo`). The distance pre-answer is postcode-level, and the server keeps a
  // pair only when it carries the postcode the finder served it with (FormServedAddressGuard), so
  // correcting line 1 to your own building on the same street keeps the point; moving the
  // address to another postcode drops it. Postcodes compare compacted (case and spaces ignored).
  const set = (key: keyof AddressValue, text: string) => {
    const { latitude: _latitude, longitude: _longitude, ...parts } = value as AddressValue & AddressGeo;
    const postcodeMoved =
      key === "postcode" && compactForFormat(text.trim()) !== compactForFormat((value.postcode ?? "").trim());
    onChange(postcodeMoved ? { ...parts, [key]: text } : { ...value, [key]: text });
  };
  // Phase 564 — a SEARCH-LED block is the same pair that has always gated the control: a declared
  // `lookup: postcode` AND a wired transport. Anything else renders exactly what it always has.
  const searchLed = lookup === "postcode" && onLookup !== undefined;
  const filled = LINES.some(({ key }) => key !== "postcode" && (value[key] ?? "").trim().length > 0);
  const linesShown = !searchLed || revealed || chosen !== undefined || filled || invalid === true;
  // Once on screen for ANY reason (a filled value, an `invalid` field), the lines latch there —
  // un-ticking `same_as` or clearing every line by hand never collapses them again (AC-4).
  if (searchLed && linesShown && !revealed) setRevealed(true);
  /** The ONE line-input markup both arms render, so a moved postcode is the same input it was. */
  const lineInput = (line: (typeof LINES)[number]) => (
    <div className="address-line" key={line.key}>
      <label className="address-sublabel" htmlFor={`${id}__${line.key}`}>
        {line.label}{line.optional ? " (optional)" : ""}
      </label>
      <input
        id={`${id}__${line.key}`}
        className={`field-input${line.mono ? " field-input-mono" : ""}`}
        type="text"
        autoComplete={line.autoComplete}
        aria-invalid={invalid && line.key !== "line2" && line.key !== "county" ? true : undefined}
        value={value[line.key] ?? ""}
        onChange={(e) => set(line.key, line.mono ? e.target.value.toUpperCase() : e.target.value)}
      />
    </div>
  );
  const postcodeLine = LINES.find((l) => l.key === "postcode")!;
  return (
    <div className="address-block" aria-describedby={describedBy}>
      {sameAs && (
        <label className="address-same">
          <input
            type="checkbox"
            checked={copied}
            onChange={(e) => onChange(e.target.checked ? { ...sameAs.value } : {})}
          />
          {`Same as the ${sameAs.label.toLowerCase()}`}
        </label>
      )}
      {searchLed && (
        <div className="address-search">
          {lineInput(postcodeLine)}
          <div className="address-lookup">
            <button
              type="button"
              id={`${id}__lookup`}
              className="lookup-button address-lookup-button"
              // Disabled until the postcode is one this form would accept, and again once THIS
              // postcode has been searched — one press, one call, and no credit spent twice on the
              // same answer. Every degraded arm leaves it live, so a retry is always one press away.
              disabled={busy || !postcodeValid || searched}
              aria-busy={busy}
              aria-describedby={notice ? noticeId : undefined}
              onClick={run}
            >
              {busy ? <><span className="lookup-spinner" aria-hidden="true" />{ADDRESS_LOOKUP_BUSY_LABEL}</> : ADDRESS_LOOKUP_LABEL}
            </button>
            {notice && (
              <p id={noticeId} className="lookup-status lookup-status-notice" role="status" aria-live="polite">
                {notice}
              </p>
            )}
            {searched && found !== null && (
              <LookupChoiceList
                id={`${id}__lookup`}
                eyebrow={ADDRESS_LOOKUP_EYEBROW}
                candidates={found.candidates}
                chosen={chosen}
                onPick={pick}
              />
            )}
          </div>
          {!linesShown && (
            <button type="button" id={`${id}__manual`} className="address-manual" onClick={() => setRevealed(true)}>
              {ADDRESS_MANUAL_LABEL}
            </button>
          )}
        </div>
      )}
      {linesShown && (
        <>
          <div className="address-line">
            {LINES.filter((l) => l.wide).map(lineInput)}
          </div>
          <div className={`address-row${searchLed ? " address-row-pair" : ""}`}>
            {LINES.filter((l) => !l.wide && !(searchLed && l.key === "postcode")).map(lineInput)}
          </div>
        </>
      )}
    </div>
  );
}
